import {
  Badge,
  Box,
  Button,
  IconButton,
  Paper,
  Popper,
  Typography,
} from '@mui/material';
import { useRecoilValue } from 'recoil';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import FacebookIcon from '@mui/icons-material/Facebook';
import XIcon from '@mui/icons-material/X';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { bodyWidth } from '../../utils/Style';
import { settingsAtom } from '../../atoms/Settings';
import { SearchBox } from '../SearchBox/SearchBox';
import { useThemeColor } from '../../hooks/useThemeColor';
import { NavMenuItem } from '../../interfaces/Settings/NavMenuItem';
import { useClickMenuItem } from './useClickMenuItem';
import { useBasket } from '../../hooks/useBasket';
import { isTokenValid } from '../../utils/Auth';

type MenuItemButtonProps = {
  menuItem: NavMenuItem;
};

const MenuItemButton = ({ menuItem }: MenuItemButtonProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const { primary, secondary, ternary } = useThemeColor();

  const onClick = useClickMenuItem();

  const vertical = menuItem.items.reduce(
    (acc, item) => acc && !item.items.length,
    true,
  );

  return (
    <Box
      onMouseEnter={(ev) => setAnchorEl(ev.currentTarget)}
      onMouseLeave={() => setAnchorEl(null)}
    >
      <Button
        onClick={() => onClick(menuItem)}
        sx={{
          color: secondary,
          fontSize: '14px',
          fontWeight: 600,
          letterSpacing: '0.5px',
          lineHeight: '20px',
          p: '15px 18px',
          height: 50,
          transition: 'color 0.5s ease',
          textTransform: 'none',
          '&:hover': {
            color: ternary,
            backgroundColor: 'transparent',
          },
        }}
      >
        {menuItem.name}
      </Button>

      <Popper open={!!anchorEl && !!menuItem.items.length} anchorEl={anchorEl}>
        <Paper
          sx={{
            display: 'flex',
            flexDirection: vertical ? 'column' : 'row',
            gap: vertical ? 0 : 5,
            p: 1.5,
          }}
        >
          {menuItem.items.map((child, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
                alignItems: 'start',
              }}
            >
              <Button
                onClick={() => onClick(child)}
                sx={{
                  color: ternary,
                  mb: child.items.length ? 0.25 : 0,
                  p: 0,
                  minWidth: 0,
                  fontWeight: 600,
                  textTransform: 'none',
                  transition: 'color 0.3s ease',
                  '&:hover': {
                    color: primary,
                    backgroundColor: 'transparent',
                  },
                }}
              >
                {child.name}
              </Button>

              {child.items.map((item, j) => (
                <Button
                  key={j}
                  onClick={() => onClick(item)}
                  sx={{
                    color: ternary,
                    textTransform: 'none',
                    p: 0,
                    minWidth: 0,
                    transition: 'color 0.3s ease',
                    '&:hover': {
                      color: primary,
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  {item.name}
                </Button>
              ))}
            </Box>
          ))}
        </Paper>
      </Popper>
    </Box>
  );
};

export const AppHeaderDesktop = () => {
  const settings = useRecoilValue(settingsAtom);

  const { primary, secondary } = useThemeColor();

  const { totalQuantity } = useBasket();

  const navigateTo = useCallback(
    (url: string) => window.open(url, '_blank'),
    [],
  );
  const navigate = useNavigate();

  const logo = settings.settingsV1.storefront_header_logo;

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          py: 1.5,
        }}
      >
        <Box
          width={bodyWidth}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 1.5,
            alignItems: 'center',
          }}
        >
          {logo ? (
            <Box
              onClick={() => navigate('')}
              component='img'
              src={logo}
              height={80}
              width='auto'
              sx={{ cursor: 'pointer' }}
            />
          ) : (
            <Typography>
              {settings.settingsV1.storefront_navbar_text}
            </Typography>
          )}

          <Box sx={{ ml: 1 }}>
            {!!settings.settingsV1.storefront_facebook_link && (
              <IconButton
                onClick={() =>
                  navigateTo(settings.settingsV1.storefront_facebook_link)
                }
              >
                <FacebookIcon sx={{ color: primary }} />
              </IconButton>
            )}

            {!!settings.settingsV1.storefront_twitter_link && (
              <IconButton
                onClick={() =>
                  navigateTo(settings.settingsV1.storefront_twitter_link)
                }
              >
                <XIcon sx={{ color: primary }} />
              </IconButton>
            )}

            {!!settings.settingsV1.storefront_instagram_link && (
              <IconButton
                onClick={() =>
                  navigateTo(settings.settingsV1.storefront_instagram_link)
                }
              >
                <InstagramIcon sx={{ color: primary }} />
              </IconButton>
            )}

            {!!settings.settingsV1.storefront_youtube_link && (
              <IconButton
                onClick={() =>
                  navigateTo(settings.settingsV1.storefront_youtube_link)
                }
              >
                <YouTubeIcon sx={{ color: primary }} />
              </IconButton>
            )}

            {!!settings.settingsV1.storefront_linkedin_link && (
              <IconButton
                onClick={() =>
                  navigateTo(settings.settingsV1.storefront_linkedin_link)
                }
              >
                <LinkedInIcon sx={{ color: primary }} />
              </IconButton>
            )}
          </Box>

          <Box sx={{ ml: 'auto' }} />

          <SearchBox placeholder='Aramak için yazınız...' width={290} />

          <Box>
            <IconButton onClick={() => navigate('/favoriler')}>
              <FavoriteBorderIcon />
            </IconButton>

            <IconButton onClick={() => navigate('/sepet')}>
              <Badge
                badgeContent={totalQuantity.toString()}
                sx={{
                  '& .MuiBadge-badge': {
                    color: secondary,
                    backgroundColor: primary,
                  },
                }}
                overlap='circular'
                style={{ transform: 'translate(25px, -15px)' }}
              />
              <ShoppingBasketIcon />
            </IconButton>

            <IconButton
              onClick={() =>
                navigate(isTokenValid() ? '/account' : '/giris-yap')
              }
            >
              <PersonOutlineIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          backgroundColor: primary,
          width: '100%',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ width: bodyWidth, display: 'flex', flexDirection: 'row' }}>
          {settings.menu.map((item, i) => (
            <MenuItemButton key={i} menuItem={item} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};
